import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal } from "./Reveal";

const QUOTES = [
  {
    quote: "Kiwi Kids transformed my child's confidence. The teachers treat every toddler with immense warmth and individual care. The progress in speech and social interaction is phenomenal!",
    author: "Priya Sharma",
    role: "Parent, Nursery",
    branch: "Gurugram",
  },
  {
    quote: "Finding a preschool that balances fun with structured early learning was vital for us. Kiwi Kids exceeds all expectations with incredible activities and transparent updates.",
    author: "Ankit Verma",
    role: "Parent, Playgroup",
    branch: "Manesar",
  },
  {
    quote: "The focus on hands-on discovery and creative expression makes my daughter look forward to school every single morning!",
    author: "Ritu Srivastava",
    role: "Parent, Senior KG",
    branch: "Uttar Pradesh",
  },
];

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const q = QUOTES[i];
  const step = (d) => setI((i + d + QUOTES.length) % QUOTES.length);

  return (
    <section id="feedback" data-testid="parent-feedback-section" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute top-20 -left-32 w-[30rem] h-[30rem] rounded-full bg-apricot/50 blur-3xl" />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <Reveal>
          <span className="inline-block text-xs font-bold tracking-[0.24em] uppercase text-peach mb-4">
            Parent Feedback
          </span>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-ink">
            Loved by parents,{" "}
            <em className="font-quote italic font-medium text-sage">cherished by children</em>
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative mt-14 bg-white border border-ink/6 rounded-[2.5rem] p-8 sm:p-12 soft-shadow-lg">
            <Quote className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 text-sun fill-sun" />
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`testimonial-${i}`}
              >
                <div className="flex justify-center gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="w-5 h-5 text-sun fill-sun" />
                  ))}
                </div>
                <p className="font-quote italic text-xl sm:text-2xl lg:text-[1.7rem] leading-snug text-ink/85">
                  “{q.quote}”
                </p>
                <footer className="mt-7">
                  <div className="font-display font-bold text-ink">{q.author}</div>
                  <div className="text-sm text-ink/55 mt-1">
                    {q.role} · {q.branch}
                  </div>
                </footer>
              </motion.blockquote>
            </AnimatePresence>

            <div className="mt-9 flex items-center justify-center gap-4">
              <button
                onClick={() => step(-1)}
                data-testid="testimonial-prev-button"
                aria-label="Previous testimonial"
                className="w-11 h-11 rounded-full border border-ink/12 flex items-center justify-center text-ink/60 hover:bg-ink hover:text-cream transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {QUOTES.map((_, d) => (
                  <button
                    key={d}
                    onClick={() => setI(d)}
                    aria-label={`Go to testimonial ${d + 1}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      d === i ? "w-7 bg-peach" : "w-2 bg-ink/15 hover:bg-ink/30"
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={() => step(1)}
                data-testid="testimonial-next-button"
                aria-label="Next testimonial"
                className="w-11 h-11 rounded-full border border-ink/12 flex items-center justify-center text-ink/60 hover:bg-ink hover:text-cream transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
